import { advancedFilterCondition, dateFilterEnum } from 'common/constants';
import { observable, action, makeObservable } from 'mobx';
import { notificationError, isSome, isWarehouse, isSuperAdminOrganization, isSeller, translate } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';

class TableFilterUtility {
	@observable visible = false;
	@observable targetElement = null;
	@observable top = 0;
	@observable left = 0;
	@observable tableWrap = null;
	bottomCoordinate = window.innerHeight;
	column = null;

	constructor({ lookupStore, listStore, queryUtility, columns, services }) {
		makeObservable(this);
		Object.assign(this, { lookupStore, listStore, queryUtility, columns, services });
		this.rootStore = this.lookupStore.rootStore;
		this.activeView = this.rootStore[this.rootStore.activeView];
	}

	@action.bound
	calculatePopupPosition = () => {
		const { x, y, height, width, right, bottom } = this.targetElement.getClientRects()[0];
		this.top = y + height;
		this.left = x;
		this.startLeftValue = x - 250;
		this.startBottomValue = bottom + 520;
		this.tableWrap = document.getElementsByClassName('k-widget k-grid')[0]
			? document.getElementsByClassName('k-widget k-grid')[0]
			: document.getElementsByClassName('table__wrap--nopager')[0];
		if (this.tableWrap != undefined) {
			if (this.startLeftValue > this.tableWrap.clientWidth / 2) {
				if (right > this.tableWrap.clientWidth + 250) {
					this.left -=
						300 -
						width +
						(right - (this.tableWrap.offsetWidth + 250)) +
						(this.tableWrap.offsetWidth - this.tableWrap.clientWidth);
				} else {
					this.left -= 300 - width;
				}
			}
			if (this.startBottomValue > this.bottomCoordinate) {
				this.top = y - (520 + height);
			}
		}
	};

	@action.bound
	onTableFilter = (column, targetElement) => {
		if (this.visible) {
			this.visible = false;
		} else {
			this.targetElement = targetElement;
			this.calculatePopupPosition();
			this.column = column;
			this.visible = true;
		}
	};

	find = async (search, sellerId) => {
		if (this.column.localFilterList) {
			const list = this.column.localFilterList;
			if (search) {
				return list.filter(item => item.toLowerCase().includes(search.toLowerCase()));
			}
			return list;
		}

		const key = this.column.dontSplit ? this.column.key : this.column.key.split('.')[0];
		const filterKey = this.column.filterKey;
		const filter = {
			pageNumber: this.column.page,
			rpp: this.column.rpp,
			orderDirection: 'asc',
			search: search != '' ? search.replace(/"/g, '') : null,
		};
		let response = null,
			retVal = null;
		if (isSome(this.column.lookupConstant)) {
			Object.assign(filter, {
				orderBy: 'name',
				...this.column.filterApiParams,
			});
			response = await this.lookupStore.getLookupService(this.column.lookupConstant).find(filter);
			// Tax code special case, in back-end too
			if (_.isArray(response.data) && response.data.length) {
				response = {
					...response,
					data: {
						item: response.data.map(item => {
							return { name: item };
						}),
					},
				};
			}
		} else {
			let serviceIdx = 0;
			if (isSome(this.column.service)) {
				serviceIdx = _.findIndex(this.services, item => {
					return item.constructor.name.toLowerCase() == this.column.service.toLowerCase();
				});
			}
			Object.assign(filter, {
				searchFields: filterKey ? filterKey : key.split('.').pop(),
				orderBy:
					this.column.filterKey != null
						? this.column.filterKey
						: _.includes(key, '.')
						? _.upperFirst(_.last(_.split(key, '.')))
						: _.upperFirst(key),
				sellerIds:
					sellerId == null && !isSeller(this.rootStore)
						? []
						: [sellerId || this.rootStore.applicationStore.currentOrganization.id],
				...this.column.filterApiParams,
			});
			response = await this.services[serviceIdx].find(filter);
		}
		if (response && response.data && response.data.item && response.data.item.length) {
			if (isSome(this.column.lookupConstant)) {
				const rawNames = _.map(response.data.item, item => _.get(item, 'name'));
				retVal = this.column.mapFilterValues ? this.column.mapFilterValues(rawNames) : rawNames;
			} else if (this.column.mapFilterValues) {
				retVal = this.column.mapFilterValues(response.data.item);
			} else {
				// Group results, eliminate duplicates
				const searchListValues = [];
				const path = filterKey ? filterKey.split('.') : key.split('.');
				const subPath = [this.column.key.split('.')[1]];
				_.each(response.data.item, item => {
					this.findObjectForObservation(item, 0, [path[path.length - 1]], searchListValues, subPath);
				});
				retVal = _.uniq(searchListValues);
			}
		}
		this.column.isAllDataFetched = response.data.item.length < filter.rpp;
		// Remove empty values
		return retVal ? _.compact(retVal) : null;
	};

	findObjectForObservation = (item, currentKeyIndex, path, finalArray, subPath) => {
		if (_.isArray(_.get(item, path[currentKeyIndex]))) {
			var currentArray = _.get(item, path[currentKeyIndex]);
			_.each(currentArray, arrayObject => {
				this.findObjectForObservation(arrayObject, 0, subPath, finalArray);
			});
		} else if (this.column.filterKey) {
			finalArray.push(_.get(item, this.column.filterKey));
		} else {
			finalArray.push(_.get(item, _.slice(path, currentKeyIndex)));
		}
	};

	remove = async () => {
		// Remove current column filter.
		this.column.filter = null;
		// Remove old filter data from local storage.
		let columnFilterIndex = _.findIndex(this.columns, element => {
			return element.key === this.column.key;
		});
		if (columnFilterIndex > -1) {
			this.columns[columnFilterIndex].filter = null;
		}
		this.visible = false;
		await this.update();
	};

	@action
	removeAll = async (initialize = true) => {
		// Remove all filters.
		_.forEach(this.columns, (column, key, col) => {
			column.filter = null;
			col[key] = column;
		});
		this.visible = false;
		delete this.queryUtility.filter.advancedSearch;
		if (initialize) {
			try {
				this.queryUtility.initialize(false);
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
				delete this.queryUtility.filter.advancedSearch;
				// NOTE: This can still break into "internal error" handler due to different error
				this.queryUtility.initialize(false);
			}
		}
	};

	update = async () => {
		let { filter, key } = this.column;
		key = this.column.advancedFilterFieldName ? this.column.advancedFilterFieldName : key;
		let advancedSearch = [];
		let oldAdvancedSearch = this.queryUtility.filter.advancedSearch;
		let currentFilterElements = oldAdvancedSearch ? [...oldAdvancedSearch] : [];
		if (currentFilterElements) {
			_.remove(
				currentFilterElements,
				filterElement =>
					filterElement.fieldName === key ||
					(key === 'fulfillmentOrderId' && filterElement.fieldName === 'onHoldReasonId') ||
					key === 'referenceNumber'
			);
		}
		if (key === 'dateCreated' && _.get(this.activeView, 'orderDateRangeViewStore.dateFieldName') === key) {
			if (filter) {
				this.activeView.orderDateRangeViewStore.dateFilterDropdownStore.value = {
					value: 'None',
					label: translate('ORDERVIEW.PLACEHOLDER.DATERANGESELECT'),
				};
			} else {
				this.activeView.orderDateRangeViewStore.setInitialValue(currentFilterElements);
			}
		}
		if (filter && filter.hasErrors) {
			const hasErrorsFilterElement = {
				condition: advancedFilterCondition.NotEquals,
				fieldName: 'onHoldReasonId',
				filterValue: [{ value: null }],
			};
			currentFilterElements.push(hasErrorsFilterElement);
		}
		if (filter && filter.freeType) {
			const freeTypeFilterElement = {
				condition: advancedFilterCondition.Contains,
				fieldName: key,
				filterValue: [{ value: filter.freeType }],
				useAlias: this.column.useAlias,
			};
			currentFilterElements.push(freeTypeFilterElement);
		}
		if (filter && filter.selected) {
			const selectedFilterElement = {
				condition: advancedFilterCondition.In,
				fieldName: key,
				filterValue: filter.selected.map(value => {
					return { Value: value };
				}),
				useAlias: this.column.useAlias,
			};

			if (filter.selected.slice().length) {
				currentFilterElements.push(selectedFilterElement);
			}
		}
		if (filter && filter.specificDate) {
			const specificDateFilterElement = {
				condition: advancedFilterCondition.Equals,
				fieldName: key,
				filterValue: [{ value: moment(filter.specificDate).format('YYYY/MM/DD') }],
			};
			currentFilterElements.push(specificDateFilterElement);
		}
		if (filter && filter.dateRangeStart && filter.dateRangeEnd) {
			const rangeStartFilterElement = {
				condition: advancedFilterCondition.GreaterEqual,
				fieldName: key,
				filterValue: [{ value: moment(filter.dateRangeStart).format('YYYY/MM/DD') }],
			};
			const rangeEndFilterElement = {
				condition: advancedFilterCondition.LesserEqual,
				fieldName: key,
				filterValue: [{ value: moment(filter.dateRangeEnd).format('YYYY/MM/DD') }],
			};
			currentFilterElements.push(rangeStartFilterElement, rangeEndFilterElement);
		}
		if (filter && filter.specificNumber) {
			const specificNumberFilterElement = {
				condition: advancedFilterCondition.In,
				fieldName: key,
				filterValue: [{ value: filter.specificNumber }],
			};

			currentFilterElements.push(specificNumberFilterElement);
		}
		if (filter && (filter.numberRangeStart || filter.numberRangeEnd)) {
			const numberRangeStartFilterElement = {
				condition: advancedFilterCondition.GreaterEqual,
				fieldName: key,
				filterValue: [{ value: filter.numberRangeStart }],
			};
			const numberRangeEndFilterElement = {
				condition: advancedFilterCondition.LesserEqual,
				fieldName: key,
				filterValue: [{ value: filter.numberRangeEnd }],
			};
			currentFilterElements.push(numberRangeStartFilterElement, numberRangeEndFilterElement);
		}
		advancedSearch = currentFilterElements;
		this.queryUtility.filter.advancedSearch = advancedSearch;
		try {
			this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			delete this.queryUtility.filter.advancedSearch;
			// NOTE: This can still break into "internal error" handler due to different error
			this.queryUtility.initialize(false);
		}
	};
}

export default TableFilterUtility;
